import keycodes from 'keycodes'
import lodash from 'lodash'
import moment from 'moment-timezone'

import {getDateFormat, getTimeFormat} from '@base/app-services/i18n-format'

import {getLocalTimezone} from '@base-core/app-utils/dateTimeFormatter'
import {LOCAL_TIMEZONE} from '@base-core/app-utils/timezoneMapping'

import type {
  CalendarSelectMode,
  CalendarSelectValue,
  WeekdayHeader,
} from './types'

const ISO_FORMAT_DATE = 'YYYY-MM-DD'
const ISO_FORMAT_TIME = 'HH:mm'
const ISO_FORMAT_TIME_WITH_SECONDS = 'HH:mm:ss'
const ISO_FORMAT_TIME_WITH_SECONDS_AND_MILISECONDS = 'HH:mm:ss.SSS'
const ISO_FORMAT_WITHOUT_TIMEZONE = 'YYYY-MM-DDTHH:mm:ss.SSS'
const ISO_FORMAT_UTC = 'YYYY-MM-DDTHH:mm:ssZ'

function getSimplifiedDateFormat(format: string) {
  return format === 'DD/MM/YYYY' ? 'D/M/YYYY' : 'M/D/YYYY'
}

function getDateFormats() {
  const format = getDateFormat()
  const simplified = getSimplifiedDateFormat(format)
  return [format, simplified, ISO_FORMAT_DATE]
}

function getComplementaryDateFormat() {
  const format = getDateFormat()
  return format === 'DD/MM/YYYY' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
}

function getComplementaryDateFormats() {
  const format = getComplementaryDateFormat()
  const simplified = getSimplifiedDateFormat(format)
  return [format, simplified]
}

function getComplementaryTimeFormat() {
  const format = getTimeFormat()
  return format === 'hh:mm A' ? 'HH:mm' : 'hh:mm A'
}

function extendTimeFormatWithSeconds(format: string) {
  return format === 'hh:mm A' ? 'hh:mm:ss A' : 'HH:mm:ss'
}

function extendTimeFormatWithSecondsAndMiliseconds(format: string) {
  return format === 'hh:mm A' ? 'hh:mm:ss.SSS A' : 'HH:mm:ss.SSS'
}

function sanitizeTimezone(timezone?: string | null | Symbol) {
  return timezone === LOCAL_TIMEZONE ? getLocalTimezone() : timezone
}

export type CalendarFilter =
  | string
  | undefined
  | null
  | {
      filter: string | undefined | null
      mode: CalendarSelectMode
    }

export type TimeInterval = 'day' | 'month' | 'year'

export function startOfDay(date?: string | Date | null) {
  if (!date) {
    return date
  }
  return moment(date).startOf('day').format(ISO_FORMAT_WITHOUT_TIMEZONE)
}

export function endOfDay(date?: string | null) {
  if (!date) {
    return date
  }
  return moment(date).endOf('day').format(ISO_FORMAT_WITHOUT_TIMEZONE)
}

export function addIntervals(
  start: Date,
  offset: number,
  interval: TimeInterval
) {
  return moment(start).add(offset, interval).toDate()
}

export function calculateNextDate(start: Date, keyCode: number): Date {
  const offset =
    (keyCode === keycodes('up') && 1) ||
    (keyCode === keycodes('down') && -1) ||
    0

  return addIntervals(start, offset, 'day')
}

export function formatDay(date: Date): number {
  return date.getDate()
}

export function addMonths(date: Date | undefined | null, count: number): Date {
  return moment(date!).clone().add(count, 'months').toDate()
}

export function getMonthsDiff(d1: Date, d2: Date): number {
  return moment(d1).month() - moment(d2).month()
}

export function startOfMonth(date: Date): Date {
  return moment(date).clone().startOf('month').toDate()
}

export function getFirstDayOfWeek(): number {
  // @ts-expect-error this overload of localeData is not compatible with firstDayOfWeek
  return moment().localeData().firstDayOfWeek()
}

export function getDaysCountForCurrentMonth(date: Date): number {
  return moment(date).daysInMonth()
}

export function addDays(date: Date, number: number): Date {
  return moment(date).clone().add(number, 'days').toDate()
}

export function subtractDays(date: Date, number: number): Date {
  return moment(date).clone().subtract(number, 'days').toDate()
}

export function isDayOutsideMonth(d1: Date, d2: Date): boolean {
  return !moment(d1).isSame(d2, 'month')
}

export function isSameDay(d1?: Date | null, d2?: Date | null): boolean {
  return moment(d1!).isSame(d2!, 'day')
}

export function nowInBrowserTimezone() {
  const d = moment().tz(moment.tz.guess())
  return new Date(
    d.year(),
    d.month(),
    d.date(),
    d.hour(),
    d.minute(),
    d.second()
  )
}

export function isToday(date: Date): boolean {
  return isSameDay(date, nowInBrowserTimezone())
}

export function isBefore(d1: Date, d2: Date): boolean {
  return moment(d1).isBefore(d2, 'day')
}

export function isAfter(d1: Date, d2: Date): boolean {
  return moment(d1).isAfter(d2, 'day')
}

export function isBetween(d: Date, d1: Date, d2: Date): boolean {
  return moment(d).isBetween(d1, d2)
}

export function formatMonthTitle(date: Date): string {
  return `${moment.months()[moment(date).month()]} ${moment(date).year()}`
}

export function getMinWeekdayNameInLocaleSpecificOrder(day: number): string {
  // As of 2.13.0 you can pass a bool as the first parameter of the weekday functions.
  // If true, the weekdays will be returned in locale specific order.
  return moment.weekdaysMin(true, day)
}

export function getLongWeekdayNameInLocaleSpecificOrder(day: number): string {
  // As of 2.13.0 you can pass a bool as the first parameter of the weekday functions.
  // If true, the weekdays will be returned in locale specific order.
  return moment.weekdays(true, day)
}

export function getArrayOfWeekdayHeaders(): Array<WeekdayHeader> {
  return lodash.range(0, 7).map((weekdayNameIndex) => ({
    title: getLongWeekdayNameInLocaleSpecificOrder(weekdayNameIndex),
    content: getMinWeekdayNameInLocaleSpecificOrder(weekdayNameIndex)[0],
  }))
}

export function dateOutsideMonthsDisplayed(
  date: Date,
  start: Date,
  monthsDisplayed: number
) {
  return !isBetween(date, start, addMonths(start, monthsDisplayed))
}

export function parseDateAndTime(data?: string | null) {
  if (!data) {
    return {date: null, time: null}
  }

  const datetime = moment(data, ISO_FORMAT_WITHOUT_TIMEZONE)
  const date = datetime.format(ISO_FORMAT_DATE)
  const time = datetime.format(ISO_FORMAT_TIME)
  return {date, time}
}

export function parseDateCustomFormat(
  date: string | undefined | null,
  format: string
) {
  if (!date) {
    return undefined
  }
  const parsed = moment(date, format, true)
  return parsed.isValid() ? parsed.toDate() : undefined
}

export function parseDate(date?: string | null): Date | undefined | null {
  if (!date) {
    return undefined
  }
  const parsed = moment(date, getDateFormats(), true)
  return parsed.isValid() ? parsed.toDate() : undefined
}

export function guessDate(date?: string | null) {
  if (!date) {
    return undefined
  }
  const parsed = moment(date, getDateFormats())
  return parsed.isValid() ? parsed.toDate() : undefined
}

export function parseDateISO(date?: string | null) {
  if (!date) {
    return null
  }
  const parsed = moment(date, ISO_FORMAT_DATE)
  return parsed.isValid() ? parsed.toDate() : null
}

export function formatDateCustomFormat(
  date: Date | undefined | null,
  format: string
): string | undefined | null {
  if (!date) {
    return null
  }
  return moment.utc(date).format(format)
}

export function formatDateISO(date?: Date | null): string | undefined | null {
  if (!date) {
    return null
  }
  return moment(date).format(ISO_FORMAT_DATE)
}

export function formatDate(date?: Date | null): string | undefined | null {
  if (!date) {
    return undefined
  }
  return moment(date).format(getDateFormat())
}

export function formatDateAndTime({date, time}: any) {
  if (!date) {
    return null
  }

  return `${date}T${time || '00:00'}`
}

export function convertIntoTimezone(
  date: string | undefined | null,
  timezone: string
): string | undefined | null {
  if (!date) {
    return null
  }
  return (
    moment
      .utc(date)
      // @ts-ignore
      .tz(sanitizeTimezone(timezone)!)
      .format(ISO_FORMAT_WITHOUT_TIMEZONE)
  )
}

export function convertIntoUTC(
  date: string | undefined | null,
  timezone: string
): string | undefined | null {
  if (!date) {
    return null
  }
  return (
    moment
      // @ts-ignore
      .tz(date, sanitizeTimezone(timezone))
      .utc()
      .format(ISO_FORMAT_UTC)
  )
}

export function isSelected(
  date: Date | undefined | null,
  value: CalendarSelectValue
): boolean {
  if (!date || !value) {
    return false
  }

  if (!Array.isArray(value)) {
    return isSameDay(date, parseDate(value))
  }

  const start = parseDate(value[0])
  if (start && isSameDay(date, start)) {
    return true
  }

  const end = parseDate(value[1])
  if (end && isSameDay(date, end)) {
    return true
  }

  return false
}

export function isHighlighted(
  date: Date | undefined | null,
  value: CalendarSelectValue
): boolean {
  if (!date || !value || value.length <= 1) {
    return false
  }

  const start = parseDate(value[0])
  const end = parseDate(value[1])

  if (start && end) {
    return isBetween(date, start, end)
  }

  if (start && !end) {
    return isAfter(date, start)
  }

  if (!start && end) {
    return isBefore(date, end)
  }

  return false
}

export function parseFilter(filter?: CalendarFilter | null): any {
  if (typeof filter === 'string' || filter === null || filter === undefined) {
    return {
      focus: parseDate(filter),
      mode: 'exact',
    }
  }

  return {
    focus: filter && parseDate(filter.filter),
    mode: filter && filter.mode,
  }
}

export function formatFilter(filter: any): CalendarFilter {
  const {focus, mode} = filter
  if (mode === 'exact') {
    return formatDate(focus)
  }

  return {
    mode,
    filter: formatDate(focus),
  }
}

export function parseValue(value: CalendarSelectValue, mode?: string): any {
  if (!Array.isArray(value)) {
    const date = parseDate(value)

    return {
      focus: date,
      mode: 'exact',
    }
  }

  const focus = parseDate(value[mode === 'end' ? 1 : 0])

  return {focus, mode}
}

export function formatValue(
  previousValue: CalendarSelectValue,
  focus: Date | undefined | null,
  mode: string
) {
  if (mode === 'exact') {
    return formatDate(focus)
  }

  if (Array.isArray(previousValue)) {
    const previousStart = previousValue[0]
    const previousEnd = previousValue[1]

    if (mode === 'start') {
      const parsedPreviousEnd = parseDate(previousEnd)
      if (
        focus &&
        previousEnd &&
        parsedPreviousEnd &&
        isAfter(focus, parsedPreviousEnd)
      ) {
        return [formatDate(focus), null]
      }
      return [formatDate(focus), previousEnd]
    }

    if (mode === 'end') {
      const parsedPreviousStart = parseDate(previousStart)
      if (
        focus &&
        previousStart &&
        parsedPreviousStart &&
        isBefore(focus, parsedPreviousStart)
      ) {
        return [null, formatDate(focus)]
      }
      return [previousStart, formatDate(focus)]
    }
  }

  return [null, null]
}

export function convertFromDarthCustomField(date?: string | null) {
  if (!date) {
    return undefined
  }
  const parsed = moment(
    date,
    [...getDateFormats(), ...getComplementaryDateFormats()],
    true
  )
  if (!parsed.isValid()) {
    return date
  }
  return formatDate(parsed.toDate())
}

export function convertFromISO(date?: string | null) {
  return formatDate(parseDateISO(date))
}

export function convertToISO(date?: string | null) {
  return formatDateISO(parseDate(date))
}

export function convertTimeFromISO(time?: string | null) {
  if (!time) {
    return null
  }
  return moment(time, ISO_FORMAT_TIME).format(getTimeFormat())
}

export function convertTimeToISO(time?: string | null) {
  if (!time) {
    return null
  }
  return moment(time, getTimeFormat()).format(ISO_FORMAT_TIME)
}

export function splitDateAndTimeDarthCustomField(data?: string | null) {
  if (!data) {
    return {date: null, time: null}
  }

  const possibleFormats = [
    `${getDateFormat()} ${getTimeFormat()}`,
    `${getDateFormat()} ${extendTimeFormatWithSeconds(getTimeFormat())}`,
    `${getDateFormat()} ${extendTimeFormatWithSecondsAndMiliseconds(
      getTimeFormat()
    )}`,
    `${getDateFormat()} ${ISO_FORMAT_TIME}`,
    // Fallback for: DarthCustomField from Professional Services integration
    `${getDateFormat()} ${ISO_FORMAT_TIME_WITH_SECONDS}`,
    `${getDateFormat()} ${ISO_FORMAT_TIME_WITH_SECONDS_AND_MILISECONDS}`,

    // This will handle British dates (european date + AM/PM)
    `${getDateFormat()} ${getComplementaryTimeFormat()}`,
    `${getDateFormat()} ${extendTimeFormatWithSeconds(
      getComplementaryTimeFormat()
    )}`,
    `${getDateFormat()} ${extendTimeFormatWithSecondsAndMiliseconds(
      getComplementaryTimeFormat()
    )}`,

    // Fallback: sometimes backend may return correct UTC format
    `${ISO_FORMAT_DATE} ${ISO_FORMAT_TIME}`,
    ISO_FORMAT_WITHOUT_TIMEZONE,
    ISO_FORMAT_UTC,

    // Fallback for: DarthCustomField do what we can to handle broken formats existing in the system
    `${getComplementaryDateFormat()} ${ISO_FORMAT_TIME}`,
    `${getComplementaryDateFormat()} ${ISO_FORMAT_TIME_WITH_SECONDS}`,
    `${getComplementaryDateFormat()} ${ISO_FORMAT_TIME_WITH_SECONDS_AND_MILISECONDS}`,

    `${getComplementaryDateFormat()} ${getComplementaryTimeFormat()}`,
    `${getComplementaryDateFormat()} ${extendTimeFormatWithSeconds(
      getComplementaryTimeFormat()
    )}`,
    `${getComplementaryDateFormat()} ${extendTimeFormatWithSecondsAndMiliseconds(
      getComplementaryTimeFormat()
    )}`,

    `${getComplementaryDateFormat()} ${getTimeFormat()}`,
    `${getComplementaryDateFormat()} ${extendTimeFormatWithSeconds(
      getTimeFormat()
    )}`,
    `${getComplementaryDateFormat()} ${extendTimeFormatWithSecondsAndMiliseconds(
      getTimeFormat()
    )}`,

    // Try parsing out at least date
    getDateFormat(),
    ISO_FORMAT_DATE,
    getComplementaryDateFormat(),
  ]

  let datetime = moment.utc(data, possibleFormats, true) // use exact match including delimiters

  if (!datetime.isValid()) {
    // I have no idea let moment guess
    datetime = moment.utc(data, possibleFormats, false) // allow guess
    if (!datetime.isValid()) {
      return {date: null, time: null}
    }
  }

  const date = datetime.format(getDateFormat())
  const time = datetime.format(getTimeFormat())
  return {date, time}
}

export function splitDateAndTimeISO(data?: string | null) {
  if (!data) {
    return {date: null, time: null}
  }
  const datetime = moment.utc(data, `${ISO_FORMAT_DATE}T${ISO_FORMAT_TIME}:00`)
  if (!datetime.isValid()) {
    return {date: null, time: null}
  }
  const date = datetime.format(ISO_FORMAT_DATE)
  const time = datetime.format(ISO_FORMAT_TIME)
  return {date, time}
}

export function joinDateAndTimeISO({date, time}: any) {
  if (!date) {
    return null
  }
  return `${date}T${time || '00:00'}:00`
}
